import React, { useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import { getVision } from '../../actions/planningAction';
import PlanningDashboard from './PlanningDashboard';
import PlanningChat from './PlanningChat';
import PlanningJournal from './PlanningJournal';
import PlanningExecute from './PlanningExecute';
import PlanningLibrary from './PlanningLibrary';
import PlanningHabits from './PlanningHabits';
import PlanningSettings from './PlanningSettings';
import { useTheme } from '../../context/ThemeContext';
import './PlanningWorkspace.css';

export default function PlanningWorkspace() {
  const { visionId } = useParams();
  const dispatch = useDispatch();
  const { activeVision, milestones, loading } = useSelector(s => s.planning);
  const { theme } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showChat,      setShowChat]      = useState(false);
  const [view,          setView]          = useState('dashboard');
  const [focusNode,      setFocusNode]      = useState(null);
  const [focusTodo,      setFocusTodo]      = useState(null);
  const [chatFocusNode,  setChatFocusNode]  = useState(null);
  const [refreshKey,     setRefreshKey]     = useState(0);

  const openChatWithNode = useCallback((node) => {
    // node may be null (e.g. "Ask Dmyor Assist" with no parent) — open plain chat.
    setChatFocusNode(node && node._id ? { ...node, ts: Date.now() } : null);
    setShowChat(true);
  }, []);

  // On mobile the chat overlays the planner. When the user applies a suggestion,
  // briefly close the chat so they can see the node land, then reopen it.
  const peekPlannerOnApply = useCallback(() => {
    if (typeof window === 'undefined') return;
    if (!window.matchMedia('(max-width: 900px)').matches) return;
    setShowChat(false);
    setTimeout(() => setShowChat(true), 2000);
  }, []);

  // Deep-link from a doc: ?view=execute&todo=<id> opens Execute and focuses it.
  useEffect(() => {
    const v = searchParams.get('view');
    const todo = searchParams.get('todo');
    if (!v && !todo) return;
    if (v) setView(v);
    if (todo) setFocusTodo({ id: todo, ts: Date.now() });
    // Consume the params so refresh/back doesn't re-trigger.
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  const refresh = useCallback(() => {
    setRefreshKey(k => k + 1);
    return dispatch(getVision(visionId)).catch(() => {});
  }, [visionId, dispatch]);

  useEffect(() => {
    refresh();
  }, [visionId]);


  if (loading && !activeVision) {
    return <div className="pw-loading">Loading...</div>;
  }
  if (!activeVision) {
    return <div className="pw-loading">Vision not found.</div>;
  }

  return (
    <div className={`pw-page pw-page--${theme}`}>
      {/* Nav */}
      <div className="pw-nav">
        <div className="pw-view-toggle">
          <button
            className={`pw-view-btn${view === 'dashboard' ? ' pw-view-btn--active' : ''}`}
            onClick={() => setView('dashboard')}
          >
            Plan
          </button>
          <button
            className={`pw-view-btn${view === 'execute' ? ' pw-view-btn--active' : ''}`}
            onClick={() => setView('execute')}
          >
            Execute
          </button>
          <button
            className={`pw-view-btn${view === 'habits' ? ' pw-view-btn--active' : ''}`}
            onClick={() => setView('habits')}
          >
            Habits
          </button>
          <button
            className={`pw-view-btn${view === 'library' ? ' pw-view-btn--active' : ''}`}
            onClick={() => setView('library')}
          >
            Library
          </button>
          <button
            className={`pw-view-btn${view === 'journal' ? ' pw-view-btn--active' : ''}`}
            onClick={() => setView('journal')}
          >
            Journal
          </button>
        </div>

        <div className="pw-nav-right">
          <button
            className={`pw-chat-toggle${!showChat && (milestones?.length || 0) === 0 ? ' pw-chat-toggle--glow' : ''}`}
            onClick={() => setShowChat(s => !s)}
          >
            {showChat ? 'Close D Assist' : 'D Assist'}
          </button>
          <button
            className={`pw-settings-btn${view === 'settings' ? ' pw-settings-btn--active' : ''}`}
            onClick={() => setView('settings')}
            title="Vision settings"
          >
            <i className="bx bx-cog" />
          </button>
        </div>
      </div>

      {/* Global vision time progress — sits on the nav/content boundary, visible in every mode */}
      {activeVision?.timeframe && activeVision?.createdAt && (() => {
        const MS = 86400000;
        const total = activeVision.timeframe * 365;
        const passed = Math.floor((Date.now() - new Date(activeVision.createdAt)) / MS);
        const left = Math.max(total - passed, 0);
        const pct = Math.min(Math.max((passed / total) * 100, 0), 100);
        return (
          <div className="pw-timebar">
            <div className="pw-timebar-track">
              <div className="pw-timebar-fill" style={{ width: `${pct}%` }} />
            </div>
            <span className="pw-timebar-label">
              <strong>{left.toLocaleString()}</strong> days left · {Math.round(pct)}%
            </span>
          </div>
        );
      })()}

      <div className="pw-content">
        {view === 'dashboard' && (
          <PlanningDashboard
            vision={activeVision}
            milestones={milestones}
            visionId={visionId}
            onRefresh={refresh}
            theme={theme}
            focusNode={focusNode}
            onOpenChatWithNode={openChatWithNode}
          />
        )}
        {view === 'execute' && (
          <PlanningExecute
            vision={activeVision}
            milestones={milestones}
            visionId={visionId}
            onRefresh={refresh}
            onFocusNode={(id) => { setFocusNode({ id, ts: Date.now() }); setView('dashboard'); }}
            focusTodo={focusTodo}
            theme={theme}
            externalRefreshKey={refreshKey}
          />
        )}
        {view === 'habits' && (
          <PlanningHabits
            visionId={visionId}
            milestones={milestones}
            theme={theme}
            onFocusNode={(id) => { setFocusNode({ id, ts: Date.now() }); setView('dashboard'); }}
            onGoToPlan={() => setView('dashboard')}
            externalRefreshKey={refreshKey}
          />
        )}
        {view === 'library' && (
          <PlanningLibrary
            visionId={visionId}
            milestones={milestones}
            theme={theme}
            externalRefreshKey={refreshKey}
          />
        )}
        {view === 'journal' && (
          <PlanningJournal
            vision={activeVision}
            visionId={visionId}
            theme={theme}
          />
        )}
        {view === 'settings' && (
          <PlanningSettings
            vision={activeVision}
            visionId={visionId}
            theme={theme}
            onUpdated={refresh}
          />
        )}

        {showChat && (
          <div className="pw-chat-drawer">
            <PlanningChat visionId={visionId} milestones={milestones} onRefresh={refresh} onFocusNode={(id) => { setFocusNode({ id, ts: Date.now() }); setView('dashboard'); }} onAfterApply={peekPlannerOnApply} initialAttachedNode={chatFocusNode} theme={theme} />
          </div>
        )}
      </div>
    </div>
  );
}
